import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { alpha, useTheme } from '@mui/material/styles'
import { DateTimePicker } from '@mui/x-date-pickers'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { fetchParentFoOptions } from '../detailApi'
import { raisedColor } from '../detailSurfaces'
import { toDateTimeValue } from '../detailDates'
import { systemValueCodeLabel } from '../../../../generic/systemValues/systemValueLabel'
import { EMPTY_DASH } from '../detailShared'

// MIC fields: the same outlined MUI `TextField` the rest of the MIC uses, label inside the border.
// A read-only value is drawn with that same box (MUI's own notched outline and shrunk label) and a
// lock at the end — the lock still says "system datum" field by field (S68); an editable field says
// it by being an input. The value is rendered as TEXT inside the input slot, not as an `<input>`
// value, so it reads and selects like the rest of the sheet.
export const StaticInput = React.forwardRef(function StaticInput ({ className, staticContent }, ref) {
  return (
    <div ref={ref} className={className} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
      {staticContent}
    </div>
  )
})

export const ReadOnlyField = ({ label, children, endAdornment }) => {
  const theme = useTheme()
  return (
    <TextField
      label={label}
      size='small'
      fullWidth
      value=' '
      InputLabelProps={{ shrink: true }}
      InputProps={{
        readOnly: true,
        inputComponent: StaticInput,
        inputProps: { staticContent: children },
        endAdornment: (
          <InputAdornment position='end'>
            {endAdornment || <LockOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />}
          </InputAdornment>
        )
      }}
      sx={{
        '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: alpha(theme.palette.text.primary, 0.14),
          borderWidth: 1
        },
        '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: 'text.secondary' }
      }}
    />
  )
}

// A touched, unsaved field: the MUI box tinted with the warning color the footer bar uses, so the
// field's mark and the footer counter read as one thing. An error wins: MUI's own error styling.
export const fieldSx = (theme, modified, error) => {
  if (error || !modified) return {}
  const warning = theme.palette.warning.main
  return {
    '& .MuiOutlinedInput-root': { backgroundColor: alpha(warning, 0.08) },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(warning, 0.6) },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: warning },
    '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: warning }
  }
}

// What goes under an editable field: the "Modified field" mark (pasada 24) and the field's error.
export const FieldNotes = ({ testId, modified, error }) => {
  const { t_ } = useSmartTranslation()
  return (
    <>
      {modified && (
        <Stack direction='row' spacing={0.5} alignItems='center' data-testid={`${testId}-modified-mark`} sx={{ mt: 0.5, ml: 1.75 }}>
          <FiberManualRecordIcon sx={{ fontSize: 8, color: 'warning.main' }} />
          <Typography variant='caption' sx={{ fontWeight: 700, color: 'warning.main' }}>{t_('Modified field')}</Typography>
        </Stack>
      )}
      {error && <Typography data-testid={`${testId}-error`} variant='caption' color='error' sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>{error}</Typography>}
    </>
  )
}

// ⚠️ LAS DOS SECCIONES SON INVISIBLES, Y ES DELIBERADO (S67/PO). El PO retiró sus rótulos —el
// candado y el lápiz ya dicen campo a campo qué se puede cambiar—, pero no el reparto: los seis
// grupos siguen envueltos en `section-editable` y `section-identity`, y el test de pertenencia se
// queda. Dos grupos se parten (planificación y origen comercial) y sus mitades llevan sufijo
// `-editable` / `-system` en el `testId`.

// A card-shaped group of the General block: a header strip (title, optionally a note) over a
// 3-column grid of fields — §6.2's "seis grupos en tarjetas" (mockup 01-general.png).
export const BlockCard = ({ testId, title, note, children }) => {
  const theme = useTheme()
  return (
    <Box
      data-testid={testId}
      sx={{
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        backgroundColor: raisedColor(theme),
        overflow: 'hidden'
      }}
    >
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='baseline'
        flexWrap='wrap'
        useFlexGap
        sx={{
          px: 2.5,
          py: 1.25,
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          backgroundColor: raisedColor(theme)
        }}
      >
        <Typography variant='caption' sx={{ fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
          {title}
        </Typography>
        {!!note && (
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>{note}</Typography>
        )}
      </Stack>
      <Box
        sx={{
          p: 2.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2.5
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

// `absence` es OPCIONAL y solo lo pasan los campos que lo han pedido: sin él, el hueco sigue
// quedándose en blanco exactamente como antes. Se añade así, y no cambiando el comportamiento por
// defecto, porque un blanco en un campo EDITABLE significa "escribe aquí" y no "no hay dato": poner
// una frase de ausencia en todos habría mentido en la mitad de la ficha.
// El guion largo con el que esta ficha declara una ausencia. Es el mismo que usan la cabecera y
// el bloque de Avance: una ausencia escrita de dos maneras distintas se lee como dos cosas.

export const FieldRow = ({ testId, label, value, editable, absence }) => {
  const empty = value === null || value === undefined || value === ''
  return (
    <Box data-testid={testId} data-editable={editable ? 'true' : 'false'}>
      <ReadOnlyField label={label}>
        {empty && !!absence
          ? <Box component='span' sx={{ color: 'text.secondary', fontWeight: 400 }}>{absence}</Box>
          : (empty ? '' : value)}
      </ReadOnlyField>
    </Box>
  )
}

// Task 29 (E2E round 2, D-A2): the value of a field fed by X2 while X2 has not landed. On OPEN the
// row arrives seconds after N1, and painting `sheet={}` meanwhile showed values the order does not
// have (Forecast unticked, an empty date); saying it is loading, with no control, shows none.
// And if X2 FAILED it is not loading any more: it says the value is not available (fix r1).
export const SheetPendingValue = ({ testId, failed }) => {
  const { t_ } = useSmartTranslation()
  return failed
    ? <Box component='span' data-testid={`${testId}-unavailable`} sx={{ color: 'text.secondary', fontWeight: 400 }}>{t_('Not available')}</Box>
    : <Box component='span' data-testid={`${testId}-loading`} sx={{ color: 'text.secondary', fontWeight: 400 }}>{t_('Loading...')}</Box>
}

// Not editable while the row is missing: the label carries the lock, not the pencil.
export const SheetPendingFieldRow = ({ testId, label, failed }) => (
  <Box data-testid={testId} data-editable='false' data-loading={failed ? 'false' : 'true'}>
    <ReadOnlyField label={label}><SheetPendingValue testId={testId} failed={failed} /></ReadOnlyField>
  </Box>
)

// MUI dropdown (browser-rendered, like PartsBlock "Status group") instead of the OS-native `<select>`,
// which looks bad on Windows laptops. The label is shrunk because the empty option shows the dash;
// the hidden native input keeps `data-testid`, the combobox div gets the `aria-label`.
export const DetailSelect = ({ testId, label, value, onChange, modified, error, children }) => {
  const theme = useTheme()
  return (
    <TextField
      select
      size='small'
      fullWidth
      value={value}
      onChange={(event) => onChange(event.target.value)}
      label={label}
      error={!!error}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ 'data-testid': `${testId}-input`, 'aria-label': label }}
      sx={fieldSx(theme, modified, error)}
    >
      {children}
    </TextField>
  )
}

// El tipo de plan es una SELECCIÓN de catálogo, no texto libre (petición del PO, pasada 26). Mismo
// contrato de edición que el resto de campos del lote: marca de modificado, contador del pie y el
// mismo camino de guardado — no es una isla con reglas propias.
export const SelectFieldRow = ({ testId, label, field, value, options, editable, modified, onChange, error }) => {
  const { t_ } = useSmartTranslation()
  // P19 (story 204): the plan type is seeded (fabrication_order_plan_type); it reads by its code, in the code's casing.
  const planTypeText = (code) => systemValueCodeLabel(t_, 'fabrication_order_plan_type', code)
  return (
    <Box data-testid={testId} data-editable={editable ? 'true' : 'false'} data-modified={modified ? 'true' : 'false'}>
      {editable
        ? (
          <>
            <DetailSelect testId={testId} label={label} value={value} onChange={(next) => onChange(field, next)} modified={modified} error={error}>
              {/* El valor actual puede no estar en el catálogo (una fila retirada después de crear la
                  orden): se ofrece igualmente para no cambiarlo a espaldas del usuario. */}
              {/* Plan type is mandatory, like in the legacy window: the empty entry only says "none yet"
                  and cannot be chosen. */}
              <MenuItem value='' disabled data-testid={`${testId}-input-option-`}>{EMPTY_DASH}</MenuItem>
              {!!value && !options.some((option) => option.code === value) && (
                <MenuItem value={value} data-testid={`${testId}-input-option-${value}`}>{planTypeText(value)}</MenuItem>
              )}
              {options.map((option) => (
                <MenuItem key={option.id ?? option.code} value={option.code} data-testid={`${testId}-input-option-${option.code}`}>{planTypeText(option.code)}</MenuItem>
              ))}
            </DetailSelect>
            <FieldNotes testId={testId} modified={modified} error={error} />
          </>
          )
        : <ReadOnlyField label={label}>{planTypeText(value)}</ReadOnlyField>}
    </Box>
  )
}

/**
 * Fila de un campo que apunta a OTRA FILA de un catálogo (hoy solo el cliente).
 *
 * ⚠️⚠️ NO ES `SelectFieldRow` CON OTRO RÓTULO, Y LA DIFERENCIA ES EL CONTRATO DE GUARDADO. El tipo
 * de plan viaja como CÓDIGO y Sun lo resuelve contra el catálogo; una asociación viaja como
 * `{ "client": { "id": N } }` y `null` para desvincular
 * (`FoDetailGeneralEditJsonMapper.java:87,190-198`). Por eso el valor que maneja este control es el
 * ID, no el código: convertir código→id al guardar metería aquí lógica de catálogo y volvería a
 * romper el caso de la fila retirada, que es exactamente el hallazgo que llevó al tipo de plan a
 * viajar por código.
 *
 * ⚠️⚠️ Y HAY UNA TRAMPA EN ESA PUERTA QUE OBLIGA A SER EXACTOS: Sun lee un objeto SIN `id` no
 * nulo como DESVINCULAR, no como error. Un `{ id: undefined }` que se colara desde aquí borraría
 * el cliente en silencio y devolvería 200. De ahí que el vacío sea una opción EXPLÍCITA de la
 * lista —desvincular es una edición que el usuario elige— y que el control no se ofrezca cuando el
 * cliente actual llega sin id: no habría forma de representarlo sin arriesgarse a borrarlo.
 *
 * La opción vacía se rotula con la MISMA palabra de ausencia que el resto de la tarjeta usa para un
 * dato que falta, en vez de inventar una segunda («sin cliente»): dos palabras para la misma
 * ausencia en la misma tarjeta se leen como dos cosas distintas.
 */
export const ReferenceFieldRow = ({ testId, label, field, value, displayValue, options, absence, editable, modified, onChange }) => {
  return (
    <Box data-testid={testId} data-editable={editable ? 'true' : 'false'} data-modified={modified ? 'true' : 'false'}>
      {editable
        ? (
          <>
            <DetailSelect testId={testId} label={label} value={value} onChange={(next) => onChange(field, next)} modified={modified}>
              <MenuItem value='' data-testid={`${testId}-input-option-`}>{absence}</MenuItem>
              {/* ⚠️⚠️ EL CLIENTE ACTUAL SE OFRECE AUNQUE NO ESTÉ EN EL CATÁLOGO, y no es un detalle:
                sin esta opción, un catálogo que todavía no ha llegado —o que ya no trae esa fila
                porque se retiró después de crear la orden— dejaría el desplegable en «sin
                informar» PARA UNA ORDEN QUE SÍ TIENE CLIENTE. Se leería como una ausencia que es
                falsa (RN8) y bastaría con tocar cualquier otro campo y guardar para no darse
                cuenta. Es la misma decisión que ya toma el desplegable de tipo de plan. */}
              {!!value && !options.some((option) => String(option.id) === String(value)) && (
                <MenuItem value={value} data-testid={`${testId}-input-option-${value}`}>{displayValue}</MenuItem>
              )}
              {options.map((option) => (
                <MenuItem key={option.id} value={String(option.id)} data-testid={`${testId}-input-option-${option.id}`}>{option.name || option.code}</MenuItem>
              ))}
            </DetailSelect>
            <FieldNotes testId={testId} modified={modified} />
          </>
          )
        // ⚠️ Sin permiso de edición el valor se lee de LA ORDEN (`displayValue`), no del catálogo:
        // buscarlo en la lista dejaría el campo en blanco mientras el catálogo no ha llegado, y en
        // blanco para siempre si el cliente de esta orden ya no está en él. RN8: un hueco en blanco
        // no dice "no hay cliente", no dice nada.
        : <ReadOnlyField label={label}>{displayValue}</ReadOnlyField>}
    </Box>
  )
}

/**
 * Parent FO selector. Same source as the legacy row (`allFabricationOrderSimpleData`, searched by
 * code) and the same save contract as the client: `{ id }` links, an explicit `null` unlinks.
 * `value` is `{ id, fabricationOrder }` or `''` (no parent, a legitimate value, not a missing one).
 */
export const PARENT_FO_SEARCH_DELAY_MS = 300

export const ParentFoFieldRow = ({ testId, label, field, value, fabricationOrderId, absence, editable, modified, onChange }) => {
  const theme = useTheme()
  const [options, setOptions] = useState([])
  const [search, setSearch] = useState('')
  // Candidates are only read while the list is open: a closed selector costs no request.
  const [open, setOpen] = useState(false)
  // One request per pause in typing, not per key; `alive` drops the answer of a search that was
  // replaced meanwhile (cleanup runs on every new `search`, so a late answer never overwrites).
  useEffect(() => {
    if (!editable || !open) return undefined
    let alive = true
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => fetchParentFoOptions(fabricationOrderId, search))
        .then((list) => { if (alive) setOptions(list || []) })
        .catch(() => { if (alive) setOptions([]) })
    }, PARENT_FO_SEARCH_DELAY_MS)
    return () => { alive = false; clearTimeout(timer) }
  }, [editable, open, fabricationOrderId, search])
  return (
    <Box data-testid={testId} data-editable={editable ? 'true' : 'false'} data-modified={modified ? 'true' : 'false'}>
      {editable
        ? (
          <>
            <Autocomplete
              size='small'
              options={options}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
            // MUI's contract: `null` is its "no value" (a frontier, like the API's).
              value={value || null}
            // The server already filtered by what was typed.
              filterOptions={(all) => all}
              getOptionLabel={(option) => option.fabricationOrder || ''}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              onInputChange={(event, text, reason) => { if (reason === 'input' || reason === 'clear') setSearch(text) }}
              onChange={(event, chosen) => onChange(field, chosen || '')}
              slotProps={{ clearIndicator: { 'data-testid': `${testId}-clear` } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  placeholder={absence}
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                  inputProps={{ ...params.inputProps, 'data-testid': `${testId}-search`, 'aria-label': label }}
                  sx={fieldSx(theme, modified)}
                />
              )}
            />
            <FieldNotes testId={testId} modified={modified} />
          </>
          )
        : <ReadOnlyField label={label}>{value ? value.fabricationOrder : absence}</ReadOnlyField>}
    </Box>
  )
}

/**
 * Fila de un campo BOOLEANO. (ADDENDUM 33 / E39, punto 4)
 *
 * ⚠ El CONTROL no es nuevo: es el mismo `Checkbox` vestido que S370 puso en las columnas
 * extendidas booleanas (antes era un `<input type='checkbox'>` crudo, el único control de la ficha
 * con el aspecto por defecto del navegador).
 *
 * ⚠ Y lo que este componente NO hace, dicho para que nadie lo "arregle" de paso: la rama de
 * columnas extendidas NO lo usa. No es olvido — allí el rótulo y el envoltorio se pintan fuera del
 * `if`, compartidos con las ramas de texto, así que meter este componente exigiría reestructurar
 * ese bloque y volver a tocar el arreglo de S370. Queda una casilla igual en dos sitios: es
 * duplicación DECLARADA, no una que nadie haya visto.
 *
 * El `testid` y el `aria-label` van en `inputProps` para que caigan sobre el input de verdad y no
 * sobre el envoltorio: es el contrato que los tests de columnas extendidas ya fijan (click sobre
 * `-input`), y sacarlo aquí no lo cambia.
 */
export const BooleanFieldRow = ({ testId, label, value, editable, modified, onChange }) => {
  const { t_ } = useSmartTranslation()
  return (
    <Box data-testid={testId} data-editable={editable ? 'true' : 'false'} data-modified={modified ? 'true' : 'false'}>
      {editable
        ? (
          <>
            <FormControlLabel
              label={label}
              sx={{ minHeight: 40, ml: 0, ...(modified ? { color: 'warning.main' } : {}) }}
              control={(
                <Checkbox
                  checked={!!value}
                  onChange={(event) => onChange(event.target.checked)}
                  size='small'
                  inputProps={{ 'data-testid': `${testId}-input`, 'aria-label': label }}
                />
              )}
            />
            <FieldNotes testId={testId} modified={modified} />
          </>
          )
        // Sin permiso se dice con PALABRA y no con una casilla deshabilitada: una casilla apagada y
        // una casilla sin marcar se parecen demasiado, y aquí la diferencia es "no puedes tocarlo"
        // frente a "vale que no". Mismo criterio que el candado del rótulo.
        : <ReadOnlyField label={label}>{value ? t_('Yes') : t_('No')}</ReadOnlyField>}
    </Box>
  )
}

export const EditableFieldRow = ({ testId, label, field, value, editable, modified, onChange, error, inputMode = 'text' }) => {
  const theme = useTheme()
  return (
    <Box data-testid={testId} data-editable={editable ? 'true' : 'false'} data-modified={modified ? 'true' : 'false'}>
      {/* Revisión del PO (pasada 24): `data-modified` bastaba para los tests y no se VEÍA. Un campo
          tocado se marca ahora en pantalla —punto y etiqueta junto al rótulo, y el propio input
          teñido— para que "esto lo has cambiado tú y aún no está guardado" se lea de un vistazo. */}
      {editable
        ? (
          <>
            <TextField
              size='small'
              fullWidth
              label={label}
              error={!!error}
              value={value === null || value === undefined ? '' : value}
              onChange={(event) => onChange(field, event.target.value)}
              inputProps={{ 'data-testid': `${testId}-input`, 'aria-label': label, inputMode }}
              sx={fieldSx(theme, modified, error)}
            />
            <FieldNotes testId={testId} modified={modified} error={error} />
          </>
          )
        : <ReadOnlyField label={label}>{value === null || value === undefined ? '' : value}</ReadOnlyField>}
    </Box>
  )
}

// The ONE picker setup of the sheet: the fixed dates and the date extended columns share it.
export const DetailDateTimePicker = ({ testId, label, value, onChange, dateTimeFormat, modified, error, clearable = true }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  return (
    <DateTimePicker
      value={toDateTimeValue(value)}
      format={dateTimeFormat}
      ampm={false}
      timeSteps={{ minutes: 1 }}
      closeOnSelect={false}
      showToolbar={false}
      localeText={{ cancelButtonLabel: t_('CANCEL'), okButtonLabel: t_('ACCEPT'), clearButtonLabel: t_('CLEAR') }}
      slotProps={{
        actionBar: { actions: clearable ? ['cancel', 'accept', 'clear'] : ['cancel', 'accept'] },
        textField: {
          variant: 'outlined',
          size: 'small',
          fullWidth: true,
          label,
          error: !!error,
          sx: fieldSx(theme, modified, error),
          inputProps: { 'data-testid': `${testId}-input`, 'aria-label': label }
        }
      }}
      onChange={onChange}
    />
  )
}

/**
 * Fila de una fecha editable QUE SE VE Y SE ELIGE CON HORA (S450, PO). Revoca la mitad del
 * addendum 10 que truncaba a día: `EditableFieldRow` seguía usándose para `targetDate`,
 * `deliveryDate` y `expirationDate` con un `<input>` de texto sobre el DÍA derivado
 * (`localDayOf`) — la MISMA regresión de fondo que el propio addendum 10 vino a arreglar una vez
 * (dos representaciones del mismo instante, ahora divergiendo en la hora en vez de en el día).
 *
 * ⚠️⚠️ ES EL MISMO CONTROL DEL LISTADO, no uno nuevo escrito para la ficha: mismo componente
 * (`DateTimePicker` de `@mui/x-date-pickers`), mismo `format.dateTime`, mismas acciones
 * Cancel/Accept/Clear (`SmartMateriaUITable.jsx`, columna `dateTimePicker`). Dos controles para el
 * mismo tipo de dato en dos pantallas de la misma aplicación es justo el defecto que ya se corrigió
 * una vez con el tipo de plan (S72) y con el cliente (regresión de paridad, arriba).
 *
 * `clearable=false` es la excepción declarada, no un olvido: Target date es obligatoria en el
 * listado (`FabricationOrder.jsx`, columna `targetDate`), así que su picker no ofrece Clear.
 */
export const DateTimeFieldRow = ({ testId, label, field, value, editable, modified, onChange, error, dateTimeFormat, formatDateTime, clearable = true, absence = '' }) => (
  <Box data-testid={testId} data-editable={editable ? 'true' : 'false'} data-modified={modified ? 'true' : 'false'}>
    {editable
      ? (
        <>
          <DetailDateTimePicker
            testId={testId} label={label} value={value} dateTimeFormat={dateTimeFormat}
            modified={modified} error={error} clearable={clearable}
            onChange={(newValue) => onChange(field, newValue)}
          />
          <FieldNotes testId={testId} modified={modified} error={error} />
        </>
        )
      : <ReadOnlyField label={label}>{formatDateTime(value) || absence}</ReadOnlyField>}
  </Box>
)

// RN32: same control/format the listing already uses per extended-column type — a light, presentational
// mirror (DATE -> date, BOOLEAN -> Yes/No, LIST -> the raw option, everything else verbatim), not a
// second implementation of SmartMateriaUITable's column renderer.
// ⚠ Fix (pasada 21, defecto 4): a `dateTimePicker` column used to print the raw ISO value it got
// from Sun — same leak as `plannedEndDate`/`order.date` below, formatted the same way (useDateFormatter).
export const formatExtendedValue = (column, rawValue, t_, formatDateTime) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') return ''
  if (column.type === 'dateTimePicker') return formatDateTime(rawValue)
  if (column.type === 'boolean') return rawValue ? t_('Yes') : t_('No')
  return rawValue
}

// CU-L2/CU-W1: 6 fixed-order groups (RN26). T1b built the read-only presentation + gating; T2 adds
// the actual edit batch on top of the same structure — RN27/RN28/RN29/RN31/RN76/RN77.
// A LIST extended column offers ITS catalogue, exactly like the listing's smartSelect
// (`useGetExtendedColumns` gives both the same `edit.source`, plain strings): free text here let a
// value outside the catalogue be typed and saved.
export const ExtendedListInput = ({ column, value, modified, onChange }) => {
  const [options, setOptions] = useState([])
  useEffect(() => {
    let discarded = false
    Promise.resolve(column.edit?.source ? column.edit.source() : [])
      .then((loaded) => { if (!discarded) setOptions(loaded || []) })
      .catch(() => {}) // the options stay [] and the current value is still offered below
    return () => { discarded = true }
  }, [column])
  return (
    <DetailSelect testId={`extended-field-${column.field}`} label={column.name} value={value} onChange={(next) => onChange(column.field, next)} modified={modified}>
      <MenuItem value='' data-testid={`extended-field-${column.field}-input-option-`}>{EMPTY_DASH}</MenuItem>
      {!!value && !options.includes(value) && <MenuItem value={value} data-testid={`extended-field-${column.field}-input-option-${value}`}>{value}</MenuItem>}
      {options.map((option) => <MenuItem key={option} value={option} data-testid={`extended-field-${column.field}-input-option-${option}`}>{option}</MenuItem>)}
    </DetailSelect>
  )
}
